import React from 'react';
import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { css } from '@emotion/react';
import { InRoomButton } from '$/components/ui/Button/InRoomButton';
import { Trans } from '@lingui/macro';
import { ApolloError } from '@apollo/client';
import config from '$/config';
import { useLoggedIn } from '$/hooks/useMe';
import { LogoAndTitleRoomArea, PoweredByFooter } from './RoomPartsBase';
import DangerouslySetInnerHTML, {
  usePropertyFilledLink,
} from '$/components/ui/EditableInput/DangerouslySetInnerHTML';
import { PlayerMode } from '../types';
import tw from 'twin.macro';

const MessageError: React.FC<{ error?: ApolloError }> = ({ error }) => {
  if (error && error.graphQLErrors.length) {
    if (error.graphQLErrors[0].extensions?.code === 'NOT_FOUND') {
      return (
        <span>
          This webinar is no longer available...
          <br />
          Are you interested in automating your own webinar content?
        </span>
      );
    }
    return (
      <span>
        We are currently upgrading our system.
        <br />
        Please try back in a few minutes. Sorry for the inconvenience!
      </span>
    );
  }
  // for Network error and other errors
  return (
    <span>
      We are having issues connecting to the service.
      <br />
      Please try again in a few minutes...
    </span>
  );
};

const PostExitRoomBody: React.FC<{ error?: ApolloError }> = withWebinarPlayerContext([
  'ewebinar',
  'set',
  'attendee',
  'isReplayExpired',
  'locale',
  'mode',
  'isEmbeddedPlayer',
])(({ error, context }) => {
  const isLoggedIn = useLoggedIn();
  const { ewebinar, set, attendee, isReplayExpired, mode, isEmbeddedPlayer } = context;

  const redirectLinkWhenMissed = usePropertyFilledLink(
    ewebinar.exitRoomSettings?.redirectLinkWhenMissed
  );
  const redirectLink = usePropertyFilledLink(ewebinar.exitRoomSettings?.redirectLink);
  const postExitRoomCTALink = usePropertyFilledLink(
    ewebinar.exitRoomSettings.postExitRoomCTA?.link
  );

  React.useEffect(() => {
    if (!ewebinar || mode === PlayerMode.edit || attendee?.isTest) return;

    if (ewebinar.exitRoomSettings?.redirectAfterExit) {
      if (redirectLinkWhenMissed && !attendee?.joinTime && !attendee?.joinReplayTime) {
        if (isEmbeddedPlayer) {
          parent.location.href = redirectLinkWhenMissed;
        } else {
          location.href = redirectLinkWhenMissed;
        }
      }

      if (redirectLink) {
        if (isEmbeddedPlayer) {
          parent.location.href = redirectLink;
        } else {
          location.href = redirectLink;
        }
      }
    }
  }, [
    ewebinar,
    redirectLinkWhenMissed,
    redirectLink,
    attendee?.isTest,
    attendee?.joinTime,
    attendee?.joinReplayTime,
    isEmbeddedPlayer,
    mode,
  ]);

  const { ctaName, ctaDescription } = React.useMemo(() => {
    let ctaName = <Trans key='name'>View Sessions</Trans>;
    let ctaDescription: React.ReactElement | null = (
      <Trans key='description'>Want to register for an upcoming session?</Trans>
    );

    const isDefaultCTAButtonNameSetting =
      !ewebinar.exitRoomSettings.postExitRoomCTA?.name ||
      ewebinar.exitRoomSettings.postExitRoomCTA?.name === 'View Sessions';

    if (!isDefaultCTAButtonNameSetting) {
      ctaName = (
        <DangerouslySetInnerHTML
          key='name'
          markdownString={ewebinar.exitRoomSettings.postExitRoomCTA!.name}
        />
      );
    }

    if (!isDefaultCTAButtonNameSetting && !ewebinar.exitRoomSettings.postExitRoomCTA.description) {
      // The user changed the name of the button but hasn't set the button description, don't show it.
      ctaDescription = null;
    }

    if (ewebinar.exitRoomSettings.postExitRoomCTA.description) {
      ctaDescription = (
        <DangerouslySetInnerHTML
          key='description'
          markdownString={ewebinar.exitRoomSettings.postExitRoomCTA!.description ?? ''}
        />
      );
    }
    return { ctaName, ctaDescription };
  }, [ewebinar.exitRoomSettings.postExitRoomCTA]);

  return (
    <div
      className='flex flex-col mb-4'
      css={css`
        .button-inroom {
          ${tw`mx-auto! min-w-20e! max-w-max!`}
        }
      `}
    >
      <div
        className='text-2xl text-center'
        css={css`
          line-height: 1.5em;
        `}
      >
        <div>
          {ewebinar ? (
            isReplayExpired ? (
              <Trans>This replay link has expired</Trans>
            ) : (
              <span>
                <Trans>This session has ended.</Trans>
              </span>
            )
          ) : (
            <MessageError error={error} />
          )}
        </div>
        {ctaDescription && <div className='mt-3e font-medium'>{ctaDescription}</div>}
      </div>
      {set && ewebinar ? (
        <InRoomButton
          className='mt-1e'
          submitting={false}
          onClick={(): void => {
            const href = postExitRoomCTALink ?? set.urls.registration;
            if (isEmbeddedPlayer) {
              window.open(href, '_blank');
            } else {
              document.location.href = href;
            }
          }}
        >
          {ctaName}
        </InRoomButton>
      ) : error &&
        error.graphQLErrors.length &&
        error.graphQLErrors[0].extensions?.code === 'NOT_FOUND' ? (
        <InRoomButton
          className='mt-2e mb-10e'
          submitting={false}
          onClick={(): void =>
            void (window.location.href = isLoggedIn
              ? `${config.MAIN_FRONTEND_URL}/home`
              : `https://${config.DOMAIN}`)
          }
        >
          <Trans>Visit eWebinar</Trans>
        </InRoomButton>
      ) : (
        <InRoomButton
          className='mt-2e mb-10e'
          submitting={false}
          onClick={(): void => {
            location.reload();
          }}
        >
          <Trans>Refresh your browser</Trans>
        </InRoomButton>
      )}
    </div>
  );
});

export class PostExitRoomParts extends LogoAndTitleRoomArea {
  public bodyComponent(): React.FC | null {
    return PostExitRoomBody;
  }

  public footerComponent(): React.FC | null {
    return PoweredByFooter;
  }
}
